/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  TextField,
  Tooltip,
} from '@mui/material';
import { Field } from 'react-final-form';

const MultiPracticeSelectField = ({
  name,
  id,
  label,
  loading,
  disabled,
  required,
  options,
  onChange,
}) => {
  const defaultIsOptionEqualToValue = (option, value) => option?.value === value?.value;

  return (
    <Box sx={{ mb: 2 }}>
      <InputLabel shrink htmlFor={id} required={required} sx={{ fontSize: '1.2em' }}>
        {label}
      </InputLabel>

      <Field
        name={name}
        render={({ input }) => (
          <FormControl variant='standard' fullWidth>
            <Autocomplete
              id={id}
              multiple
              disableCloseOnSelect
              disabled={disabled}
              loading={loading}
              required={required}
              getOptionKey={(option) => option.value}
              options={[...new Map(options.map((o) => [o.label, o])).values()].sort((a, b) =>
                a.label.localeCompare(b.label)
              )}
              value={input.value || []}
              onChange={(event, newValue) => {
                input.onChange(newValue);
                if (onChange) {
                  onChange(event, newValue);
                }
              }}
              isOptionEqualToValue={defaultIsOptionEqualToValue}
              getOptionLabel={(option) => option.label}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.value}
                    label={
                      <Tooltip title={option.label}>
                        <Box
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 100,
                          }}
                        >
                          {option.label}
                        </Box>
                      </Tooltip>
                    }
                    {...getTagProps({ index })}
                    size='small'
                    variant='outlined'
                    sx={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ccc',
                      color: '#000',
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size='small'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && <CircularProgress color='primary' size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
        )}
      />
    </Box>
  );
};

export default MultiPracticeSelectField;

MultiPracticeSelectField.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

MultiPracticeSelectField.defaultProps = {
  loading: false,
  required: false,
  disabled: false,
  options: [],
  onChange: undefined,
};
